import { Component, inject, signal } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { StockService } from '@shared/services/entities/stock.service';
import { UploadDownloadService } from '@shared/services/upload-download.service';
import { CustodyService, LogisticsCatalogs, errMsg, fmtDate } from '../logistics.service';

export type CustodyType = 'RESGUARDO' | 'DEVOLUCION';
export interface CustodyFormData { type?: CustodyType; scopePlaces: string[] | null; }

/**
 * Resguardo y devolución extraordinaria de insumos.
 *
 *  · RESGUARDO: piezas disponibles que quedan a cargo de una persona o área;
 *    no se pueden usar en un folio hasta que se devuelvan.
 *    También admite material sin etiqueta (nunca estuvo en sistema).
 *  · DEVOLUCIÓN extraordinaria: material que regresa tarde.
 *      – Con nuestra etiqueta: se muestra el folio en que salió y si es de
 *        otra cuenta (otro hospital).
 *      – Sin etiqueta: nunca estuvo en sistema; se captura código, lote y
 *        foto, y se revisa si el código existe en otras cuentas.
 *
 * Son dos formularios separados (se abren con su propio botón). El
 * supervisor / coordinador es quien registra; los hospitales salen de sus
 * parámetros (Usuarios → Ubicaciones) y solo son de tipo HOSPITAL.
 */
@Component({
  selector: 'app-custody-form-dialog',
  standalone: true,
  imports: [FormsModule, NgTemplateOutlet, MatDialogModule, MatFormFieldModule, MatInputModule, MatButtonModule,
    MatIconModule, MatCheckboxModule, MatTooltipModule, MatProgressSpinnerModule, FormDialogShellComponent, AutoSelectComponent],
  styleUrl: '../logistics-dialog.scss',
  styles: [`
    .untagged { grid-column: 1 / -1; display: grid; grid-template-columns: 1.6fr 1fr 90px auto auto; gap: 10px; align-items: center;
      @media (max-width: 760px) { grid-template-columns: 1fr 1fr; } }
    .photo-name { font-size: 11.5px; color: #555; max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; }
  `],
  template: `
<app-form-dialog-shell [icon]="type === 'RESGUARDO' ? 'shield' : 'assignment_return'"
  [heading]="type === 'RESGUARDO' ? 'Nuevo resguardo de material' : 'Nueva devolución extraordinaria'"
  [saving]="saving()" saveLabel="Registrar y avisar" saveIcon="send" formId="custody-form" (cancelled)="ref.close(false)">
<form id="custody-form" (ngSubmit)="save()">
  <div class="form-grid">
    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Supervisor / coordinador</mat-label>
      <mat-icon matPrefix>badge</mat-icon>
      <input matInput name="supervisor" [value]="supervisorName" readonly>
      <mat-hint>Se registra con tu usuario.</mat-hint>
    </mat-form-field>

    <app-auto-select label="Hospital" name="place" [(ngModel)]="place" (changed)="onPlace()" [options]="placeOptions" [labelFn]="placeLabel"></app-auto-select>
    <app-auto-select [label]="type === 'RESGUARDO' ? 'Locker / in-house de donde salen' : 'Locker / in-house donde se recibe'" name="warehouse"
      [(ngModel)]="warehouse" (changed)="loadPieces()" [options]="warehouseOptions" [labelFn]="warehouseLabel"
      [allowEmpty]="type === 'DEVOLUCION'" emptyLabel="— Por definir —"></app-auto-select>
    @if (!placeOptions.length) {
      <div class="hint-box hint-box--warn"><mat-icon>info</mat-icon>
        <span>No tienes hospitales asignados. Pide a un administrador que te los agregue en Usuarios → Ubicaciones.</span></div>
    }

    @if (type === 'RESGUARDO') {
      <mat-form-field appearance="outline">
        <mat-label>Responsable (nombre)</mat-label>
        <input matInput name="responsibleName" [(ngModel)]="responsibleName" maxlength="120" autocomplete="off">
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Área / servicio</mat-label>
        <input matInput name="responsibleArea" [(ngModel)]="responsibleArea" maxlength="120" autocomplete="off">
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Fecha compromiso</mat-label>
        <input matInput type="date" name="commitmentDate" [(ngModel)]="commitmentDate" [min]="today" required>
        <mat-hint>Cuándo se regresa el material</mat-hint>
      </mat-form-field>

      <div class="section-title"><mat-icon>nfc</mat-icon> Piezas en resguardo <span class="spacer"></span>
        <span class="counter">{{ selected.size }} seleccionada(s)</span></div>
      <div class="scan-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Escanear etiqueta RFID</mat-label>
          <mat-icon matPrefix>qr_code_scanner</mat-icon>
          <input matInput name="scan" [(ngModel)]="scan" autocomplete="off" (keydown.enter)="$event.preventDefault(); scanResguardo()">
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Filtrar</mat-label>
          <mat-icon matPrefix>search</mat-icon>
          <input matInput name="filter" [(ngModel)]="filter" autocomplete="off">
        </mat-form-field>
      </div>
      @if (scanMsg) { <div class="hint-box hint-box--danger"><mat-icon>error</mat-icon><span>{{ scanMsg }}</span></div> }
      <div class="pieces">
        <table>
          <thead><tr><th></th><th>Código</th><th>Descripción</th><th>Lote</th><th>Caducidad</th><th>Etiqueta</th></tr></thead>
          <tbody>
            @for (p of visible; track p.id) {
              <tr [class.is-on]="selected.has(p.id)">
                <td><mat-checkbox color="primary" [checked]="selected.has(p.id)" (change)="toggle(p.id)"></mat-checkbox></td>
                <td>{{ p._code }}</td><td>{{ p._description }}</td><td>{{ p.batch || '—' }}</td><td>{{ p._exp }}</td><td class="tag">{{ p.tagRfid || '—' }}</td>
              </tr>
            }
            @if (!visible.length) {
              <tr><td colspan="6" class="empty">{{ !warehouse ? 'Elige el locker / in-house.' : loadingPieces ? 'Cargando…' : 'Sin piezas disponibles.' }}</td></tr>
            }
          </tbody>
        </table>
      </div>

      <ng-container [ngTemplateOutlet]="untaggedForm"></ng-container>
      @if (items.length) {
        <div class="pieces">
          <table>
            <thead><tr><th>Código</th><th>Descripción</th><th>Lote</th><th>Pzas</th><th>Hallazgo</th><th></th></tr></thead>
            <tbody>
              @for (it of items; track $index; let i = $index) {
                <tr class="is-flag">
                  <td>{{ it.code || '—' }}</td><td>{{ it.description || '—' }}</td><td>{{ it.batch || '—' }}</td><td>{{ it.quantity || 1 }}</td>
                  <td><span class="flag">Nunca estuvo en sistema</span>
                    @if (it.matches?.length) { <span class="sub">Ese código está en: {{ matchesText(it) }}</span> }
                    @if (it.photoName) { <span class="sub">📷 {{ it.photoName }}</span> }</td>
                  <td><button mat-icon-button type="button" color="warn" matTooltip="Quitar" (click)="items.splice(i, 1)"><mat-icon>delete</mat-icon></button></td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    } @else {
      <!-- Devolución: renglones con etiqueta y sin etiqueta -->
      <div class="section-title"><mat-icon>sell</mat-icon> Con nuestra etiqueta</div>
      <div class="scan-row">
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Escanear / escribir etiqueta RFID</mat-label>
          <mat-icon matPrefix>qr_code_scanner</mat-icon>
          <input matInput name="tagScan" [(ngModel)]="scan" autocomplete="off" maxlength="40" (keydown.enter)="$event.preventDefault(); addTagged()">
        </mat-form-field>
        <button mat-stroked-button type="button" (click)="addTagged()" [disabled]="looking() || !scan.trim()">
          @if (looking()) {<mat-spinner diameter="16"></mat-spinner>} @else {<mat-icon>search</mat-icon>} Buscar</button>
      </div>
      @if (scanMsg) { <div class="hint-box hint-box--warn"><mat-icon>info</mat-icon><span>{{ scanMsg }}</span></div> }

      <ng-container [ngTemplateOutlet]="untaggedForm"></ng-container>

      <div class="pieces">
        <table>
          <thead><tr><th>Tipo</th><th>Código</th><th>Descripción</th><th>Lote</th><th>Pzas</th><th>Origen / hallazgo</th><th></th></tr></thead>
          <tbody>
            @for (it of items; track $index; let i = $index) {
              <tr [class.is-flag]="it.otherAccount || !it.tagged">
                <td>{{ it.tagged ? 'Etiquetado' : 'Sin etiqueta' }}@if (it.tagged) {<span class="sub tag">{{ it.tagRfid }}</span>}</td>
                <td>{{ it.code || '—' }}</td><td>{{ it.description || '—' }}</td><td>{{ it.batch || '—' }}</td><td>{{ it.quantity || 1 }}</td>
                <td>
                  @if (it.tagged) {
                    Salió en folio <b>{{ it.foundFolio || 'sin folio' }}</b>
                    <span class="sub">{{ it.foundWarehouseLabel }} · {{ it.stockStatus }}</span>
                    @if (it.otherAccount) { <span class="sub flag">De otra cuenta: {{ it.foundPlaceName }}</span> }
                  } @else {
                    <span class="flag">Nunca estuvo en sistema</span>
                    @if (it.matches?.length) { <span class="sub">Ese código está en: {{ matchesText(it) }}</span> }
                    @else { <span class="sub">{{ it.inCatalog === false ? 'El código no está en catálogo' : 'Sin existencias en otras cuentas' }}</span> }
                    @if (it.photoName) { <span class="sub">📷 {{ it.photoName }}</span> }
                  }
                </td>
                <td><button mat-icon-button type="button" color="warn" matTooltip="Quitar" (click)="items.splice(i, 1)"><mat-icon>delete</mat-icon></button></td>
              </tr>
            }
            @if (!items.length) { <tr><td colspan="7" class="empty">Agrega lo que se devolvió.</td></tr> }
          </tbody>
        </table>
      </div>
    }

    <mat-form-field appearance="outline" class="full-col">
      <mat-label>Notas</mat-label>
      <textarea matInput rows="2" name="notes" [(ngModel)]="notes" maxlength="1000"></textarea>
    </mat-form-field>
  </div>
</form>

<!-- Sin etiqueta (nunca estuvo en sistema): igual en resguardo y devolución -->
<ng-template #untaggedForm>
      <div class="section-title"><mat-icon>label_off</mat-icon> Sin etiqueta (nunca estuvo en sistema)</div>
      <div class="untagged">
        <app-auto-select label="Código" name="uProduct" [(ngModel)]="uProduct" [options]="cat.products" [labelFn]="cat.productLabel" [allowEmpty]="true" emptyLabel="— No está en catálogo —"></app-auto-select>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>{{ uProduct ? 'Lote' : 'Código y lote (texto)' }}</mat-label>
          <input matInput name="uText" [(ngModel)]="uText" autocomplete="off" maxlength="80" [placeholder]="uProduct ? 'Lote' : 'Ej. 172015 / L123'">
        </mat-form-field>
        <mat-form-field appearance="outline" subscriptSizing="dynamic">
          <mat-label>Piezas</mat-label>
          <input matInput type="number" min="1" name="uQty" [(ngModel)]="uQty">
        </mat-form-field>
        <span>
          <input #photo type="file" accept="image/*" capture="environment" hidden (change)="uPhoto = photo.files?.[0] ?? null">
          <button mat-icon-button type="button" matTooltip="Foto del insumo" (click)="photo.click()"><mat-icon>photo_camera</mat-icon></button>
          @if (uPhoto) { <span class="photo-name">{{ uPhoto.name }}</span> }
        </span>
        <button mat-stroked-button type="button" (click)="addUntagged()" [disabled]="looking() || (!uProduct && !uText.trim())"><mat-icon>add</mat-icon> Agregar</button>
      </div>
</ng-template>
</app-form-dialog-shell>
  `,
})
export class CustodyFormDialogComponent {
  readonly data = inject<CustodyFormData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<CustodyFormDialogComponent>);
  readonly cat = inject(LogisticsCatalogs);
  private readonly service = inject(CustodyService);
  private readonly stockService = inject(StockService);
  private readonly uploads = inject(UploadDownloadService);
  private readonly msg = inject(CustomMessageService);

  readonly saving = signal(false);
  readonly looking = signal(false);
  readonly type: CustodyType = this.data.type ?? 'RESGUARDO';
  place: string | null = null;
  warehouse: string | null = null;
  responsibleName = '';
  responsibleArea = '';
  commitmentDate = '';
  readonly today = localDay(new Date());
  notes = '';
  scan = '';
  scanMsg = '';
  filter = '';

  // Resguardo
  loadingPieces = false;
  pieces: any[] = [];
  selected = new Set<string>();

  // Devolución
  items: any[] = [];
  uProduct: string | null = null;
  uText = '';
  uQty = 1;
  uPhoto: File | null = null;

  /** En el selector: solo el alias del hospital y solo el nombre del locker. */
  readonly placeLabel = (p: any): string => String(p?.alias || p?.name || '');
  readonly warehouseLabel = (w: any): string => String(w?.name || '');

  /** Quien registra (supervisor / coordinador). El API lo vuelve a tomar del token. */
  readonly supervisorName = sessionStorage.getItem('userName') || sessionStorage.getItem('userEmail') || '';

  /** Solo hospitales y solo los de los parámetros del usuario (sin parámetros = ninguno; administrador = todos). */
  readonly placeOptions = this.cat.places
    .filter(p => String(p.type ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toUpperCase() === 'HOSPITAL')
    .filter(p => !this.data.scopePlaces || this.data.scopePlaces.includes(String(p.id)))
    .sort((a, b) => this.placeLabel(a).localeCompare(this.placeLabel(b)));
  get warehouseOptions(): any[] { return this.cat.warehouses.filter(w => w.place === this.place); }
  get visible(): any[] {
    const q = this.filter.trim().toLowerCase();
    return q ? this.pieces.filter(p => p._search.includes(q)) : this.pieces;
  }

  constructor() {
    if (this.placeOptions.length === 1) { this.place = this.placeOptions[0].id; this.onPlace(); }
  }

  onPlace(): void {
    if (this.warehouse && this.cat.warehousesById[this.warehouse]?.place !== this.place) this.warehouse = null;
    if (!this.warehouse && this.warehouseOptions.length === 1) this.warehouse = this.warehouseOptions[0].id;
    void this.loadPieces();
  }

  async loadPieces(): Promise<void> {
    this.selected.clear(); this.pieces = [];
    if (this.type !== 'RESGUARDO' || !this.warehouse) return;
    this.loadingPieces = true;
    try {
      const r: any = await this.stockService.search({ searchType: 'default', condition: { deleted: false, warehouse: this.warehouse, status: 'Disponible' } });
      this.pieces = ((r?.data ?? []) as any[]).map(s => {
        const p = this.cat.productsById[s.product] ?? {};
        return { ...s, _code: p.code ?? '', _description: p.description ?? '', _exp: fmtDate(s.expiration, false) || '—',
                 _search: [p.code, p.description, s.batch, s.tagRfid].join(' ').toLowerCase() };
      }).sort((a, b) => String(a._code).localeCompare(String(b._code)));
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudieron cargar las piezas.'), dismissBtn: true });
    } finally {
      this.loadingPieces = false;
    }
  }

  toggle(id: string): void { this.selected.has(id) ? this.selected.delete(id) : this.selected.add(id); }

  scanResguardo(): void {
    const tag = this.scan.trim().toUpperCase(); this.scan = '';
    if (!tag) return;
    const p = this.pieces.find(x => String(x.tagRfid ?? '').toUpperCase() === tag);
    this.scanMsg = p ? '' : `La etiqueta ${tag} no está disponible en este locker.`;
    if (p) this.selected.add(p.id);
  }

  async addTagged(): Promise<void> {
    const tag = this.scan.trim().toUpperCase();
    if (!tag) return;
    if (this.items.some(i => i.tagged && i.tagRfid === tag)) { this.scanMsg = `La etiqueta ${tag} ya está en la lista.`; this.scan = ''; return; }
    this.looking.set(true);
    try {
      const r = await this.service.lookup({ place: this.place, tag });
      if (!r?.found) {
        this.scanMsg = `La etiqueta ${tag} no existe en el sistema: agrégala como "sin etiqueta" con su código y foto.`;
        return;
      }
      this.scanMsg = '';
      this.items.push({
        tagged: true, tagRfid: tag, stock: r.stock, product: r.product, code: r.code, description: r.description,
        batch: r.batch ?? null, expiration: r.expiration ?? null, quantity: 1, stockStatus: r.status,
        foundFolio: r.folio ?? null, foundPlace: r.place ?? null, foundPlaceName: r.placeName ?? '',
        foundWarehouse: r.warehouse ?? null, foundWarehouseLabel: r.warehouseLabel ?? '', otherAccount: !!r.otherAccount,
      });
      this.scan = '';
    } catch (err) {
      this.scanMsg = errMsg(err, 'No se pudo buscar la etiqueta.');
    } finally {
      this.looking.set(false);
    }
  }

  async addUntagged(): Promise<void> {
    const prod = this.uProduct ? this.cat.productsById[this.uProduct] : null;
    let code = prod?.code ?? '';
    let batch = this.uText.trim();
    if (!prod) {
      const [c, ...rest] = batch.split(/[\/\s]+/);
      code = (c ?? '').trim(); batch = rest.join(' ').trim();
    }
    if (!code) return;
    this.looking.set(true);
    try {
      const r = await this.service.lookup({ place: this.place, code, batch: batch || undefined }).catch(() => null);
      let photo: string | null = null;
      if (this.uPhoto) {
        try { photo = (await this.uploads.upload({ path: 'custody', file: this.uPhoto })).filename; }
        catch { await this.msg.open({ title: 'Foto', type: 'warning', message: 'No se pudo subir la foto; el renglón se agrega sin ella.', dismissBtn: true }); }
      }
      this.items.push({
        tagged: false, product: prod?.id ?? r?.product ?? null, code, description: prod?.description ?? r?.description ?? '',
        batch: batch || null, quantity: Math.max(1, Math.floor(Number(this.uQty) || 1)),
        photo, photoName: photo ? this.uPhoto?.name : null,
        inCatalog: r?.inCatalog ?? !!prod, matches: r?.matches ?? [],
      });
      this.uProduct = null; this.uText = ''; this.uQty = 1; this.uPhoto = null;
    } finally {
      this.looking.set(false);
    }
  }

  matchesText(it: any): string {
    return (it.matches ?? []).map((m: any) => `${m.warehouseLabel || m.placeName} (${m.count})`).join(', ');
  }

  async save(): Promise<void> {
    const resg = this.type === 'RESGUARDO';
    const problem = !this.place ? 'Elige el hospital.'
      : resg && !this.warehouse ? 'Elige el locker / in-house de donde salen las piezas.'
      : resg && !this.responsibleName.trim() ? 'Escribe el nombre del responsable del resguardo.'
      : resg && !this.commitmentDate ? 'Indica la fecha compromiso para regresar el material.'
      : resg && this.commitmentDate < this.today ? 'La fecha compromiso no puede ser anterior a hoy.'
      : resg && !this.selected.size && !this.items.length ? 'Selecciona al menos una pieza o agrega material sin etiqueta.'
      : !resg && !this.items.length ? 'Agrega al menos un insumo devuelto.' : null;
    if (problem) { await this.msg.open({ title: 'Revisa el registro', type: 'warning', message: problem, dismissBtn: true }); return; }

    const extra = this.items.map(({ photoName, ...it }) => it);
    const items = resg
      ? [...this.pieces.filter(p => this.selected.has(p.id)).map(p => ({
          tagged: true, stock: p.id, product: p.product, code: p._code, description: p._description,
          batch: p.batch ?? null, expiration: p.expiration ?? null, tagRfid: p.tagRfid ?? null })), ...extra]
      : extra;
    this.saving.set(true);
    try {
      const r: any = await this.service.create({
        type: this.type, place: this.place, warehouse: this.warehouse || null,
        responsibleName: resg ? this.responsibleName.trim() : null, responsibleArea: resg ? (this.responsibleArea.trim() || null) : null,
        commitmentDate: resg ? this.commitmentDate : null,
        items, notes: this.notes.trim() || null,
      });
      const folio = r?.data?.[0]?.folio ?? '';
      await this.msg.open({ title: '¡Registrado!', type: 'success', duration: 3000,
        message: `${resg ? 'Resguardo' : 'Devolución'} <b>${folio}</b> registrado. Se avisó al coordinador y a mesa de control.` });
      this.ref.close(true);
    } catch (err) {
      await this.msg.open({ title: 'Error', type: 'error', message: errMsg(err, 'No se pudo registrar.'), dismissBtn: true });
    } finally {
      this.saving.set(false);
    }
  }
}

function localDay(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
